import { useApi } from '@/api/useApi'
import API from '@/api/apiUrls'
import { computed, ref } from 'vue'
import { defineStore } from 'pinia'
import { useBaseStore } from '@haskoning/vue-basestore'
import { useDialog } from '@haskoning/vue-basestore/dist/composables/dialog'
import { useAuthorizationStore } from '@/stores/authorization'
import type { Calculation } from '@/stores/type/calculation.type'
import { useSelectedItemMixin } from '@/stores/mixins/useSelectedItemMixin'
import type {
  BuildingBlockSelectionSwitchNotice,
  DesignLinkImportNotice,
  DesignLinkSwitchNotice,
  TransportConfigSwitchNotice
} from '@/components/building-block-form/utils/switchValidation'

const { api } = useApi()
const baseUrl = API.CALCULATION.INDEX
const duplicateURL = API.CALCULATION.DUPLICATE
const runCalculationURL = API.CALCULATION.RUN_CALCULATION
const resetCalculationURL = API.CALCULATION.RESET_CALCULATION
const validateSwitchDatabaseURL = API.CALCULATION.VALIDATE_SWITCH_DATABASE

export const useCalculationStore = defineStore('calculation', () => {
  const {
    showDialog: duplicateDialog,
    dialogItem: duplicateDialogItem,
    openDialog: openDuplicateDialog,
    closeDialog: closeDuplicateDialog
  } = useDialog()

  const authorizationStore = useAuthorizationStore()

  const canAddCalculation = computed(() => {
    return authorizationStore.hasServicePermission({
      authSource: 'ProjectUser',
      allowed: ['add_calculation']
    })
  })

  const canChangeCalculation = computed(() => {
    return authorizationStore.hasServicePermission({
      authSource: 'ProjectUser',
      allowed: ['change_calculation']
    })
  })

  const canDeleteCalculation = computed(() => {
    return authorizationStore.hasServicePermission({
      authSource: 'ProjectUser',
      allowed: ['delete_calculation']
    })
  })

  const baseStore = useBaseStore<Calculation>({ service: baseUrl, api })
  const selectedItemMixin = useSelectedItemMixin(baseStore)
  const hasMissingDatabaseSwitchIssues = ref(false)
  const designLinkSwitchNotice = ref<DesignLinkSwitchNotice | null>(null)
  const designLinkImportNotice = ref<DesignLinkImportNotice | null>(null)
  const buildingBlockSelectionSwitchNotice = ref<BuildingBlockSelectionSwitchNotice | null>(null)
  const transportConfigSwitchNotice = ref<TransportConfigSwitchNotice | null>(null)

  const createNewCalculation = (project: number): Calculation => {
    return {
      id: 0,
      project,
      name: '',
      type: '',
      phase: '',
      lifetime: 0,
      database: null,
      status: '',
      created: '',
      modified: '',
      favorite: false
    }
  }

  const filterFields = ref(['name', 'type', 'phase', 'lifetime'])

  const fieldsFiltered = computed(() => {
    return baseStore.fieldsList.value.filter((field) =>
      filterFields.value.includes(field.parameter)
    )
  })

  const duplicate = async (item: Calculation) => {
    try {
      baseStore.writeLoading.value = true
      const responseData = await api.service(duplicateURL(item.id)).post(item)
      if (Array.isArray(responseData)) {
        baseStore.addItems(responseData)
      } else {
        baseStore.addItems([responseData])
      }
      return responseData
    } catch (error) {
      baseStore.setSnackbarMessage('Failed to duplicate calculation', 'error')
      console.error(error)
      return null
    } finally {
      baseStore.writeLoading.value = false
    }
  }

  const runCalculation = async (calculationId: number, project: number) => {
    try {
      baseStore.writeLoading.value = true
      const responseData = await api
        .service(runCalculationURL(calculationId))
        .post({ project: project })
      return responseData
    } catch (error) {
      baseStore.setSnackbarMessage('Failed to run calculation', 'error')
      console.error(error)
      return null
    } finally {
      baseStore.writeLoading.value = false
    }
  }

  const resetCalculation = async (calculationId: number, project: number) => {
    try {
      baseStore.writeLoading.value = true
      const responseData = await api
        .service(resetCalculationURL(calculationId))
        .post({ project: project, reset: true })
      return responseData
    } catch (error) {
      baseStore.setSnackbarMessage('Failed to reset calculation', 'error')
      console.error(error)
      return null
    } finally {
      baseStore.writeLoading.value = false
    }
  }

  const validateSwitchDatabase = async (
    calculationId: number,
    project: number,
    targetDatabase: number
  ) => {
    try {
      baseStore.writeLoading.value = true
      const responseData = await api
        .service(validateSwitchDatabaseURL(calculationId))
        .post({ project, target_database: targetDatabase })
      return responseData
    } catch (error) {
      baseStore.setSnackbarMessage('Failed to validate database switch', 'error')
      console.error(error)
      return null
    } finally {
      baseStore.writeLoading.value = false
    }
  }

  const duplicateSelectedItems = () => {
    openDuplicateDialog()
  }

  const bulkDuplicate = async (items: Calculation[]) => {
    try {
      baseStore.writeLoading.value = true
      const promises = items.map((item) => duplicate(item))
      const results = await Promise.all(promises)
      closeDuplicateDialog()
      baseStore.setSnackbarMessage('Calculations duplicated successfully', 'success')
      return results.filter((result) => result !== null)
    } catch (error) {
      baseStore.setSnackbarMessage('Failed to duplicate calculations', 'error')
      console.error(error)
      return []
    } finally {
      baseStore.writeLoading.value = false
    }
  }

  const bulkDelete = async (items: Calculation[]) => {
    try {
      baseStore.writeLoading.value = true
      await baseStore.bulkDelete(items)
      baseStore.setSnackbarMessage('Calculations deleted successfully', 'success')
      baseStore.selectedItems.value = []
      return items
    } catch (error) {
      baseStore.setSnackbarMessage('Failed to delete calculations', 'error')
      console.error(error)
      return []
    } finally {
      baseStore.writeLoading.value = false
    }
  }

  const toggleDuplicateDialog = (value: boolean | undefined): void => {
    duplicateDialog.value = value ?? !duplicateDialog.value
  }

  function resetAll() {
    baseStore.closeCreateUpdateDialog()
    baseStore.closeDeleteDialog()
    closeDuplicateDialog()
    hasMissingDatabaseSwitchIssues.value = false
    designLinkSwitchNotice.value = null
    designLinkImportNotice.value = null
    buildingBlockSelectionSwitchNotice.value = null
    transportConfigSwitchNotice.value = null
    baseStore.reset()
  }

  const setHasMissingDatabaseSwitchIssues = (value: boolean) => {
    hasMissingDatabaseSwitchIssues.value = value
  }

  const setDesignLinkSwitchNotice = (value: DesignLinkSwitchNotice | null) => {
    designLinkSwitchNotice.value = value
  }

  const setDesignLinkImportNotice = (value: DesignLinkImportNotice | null) => {
    designLinkImportNotice.value = value
  }

  const clearDesignLinkImportNoticeForQuantityRow = (quantityRowId: number) => {
    const notice = designLinkImportNotice.value
    if (!notice) return
    const changedRows = notice.changedRows.filter(
      (row) => Number(row.quantity_row_id) !== quantityRowId
    )
    const missingRows = notice.missingRows.filter(
      (row) => Number(row.quantity_row_id) !== quantityRowId
    )
    if (!changedRows.length && !missingRows.length) {
      designLinkImportNotice.value = null
      return
    }
    designLinkImportNotice.value = { ...notice, changedRows, missingRows }
  }

  const setBuildingBlockSelectionSwitchNotice = (
    value: BuildingBlockSelectionSwitchNotice | null
  ) => {
    buildingBlockSelectionSwitchNotice.value = value
  }

  const setTransportConfigSwitchNotice = (value: TransportConfigSwitchNotice | null) => {
    transportConfigSwitchNotice.value = value
  }

  const pruneDesignLinkSwitchNoticeByBlockIds = (blockIds: number[]) => {
    const notice = designLinkSwitchNotice.value
    if (!notice) return

    const allowedBlockIds = new Set<number>()

    for (const blockId of blockIds) {
      const buildingBlockId = Number(blockId)
      if (buildingBlockId > 0) {
        allowedBlockIds.add(buildingBlockId)
      }
    }

    const missingRows = notice.missingRows.filter((row) =>
      allowedBlockIds.has(Number(row.block_id))
    )
    const changedRows = notice.changedRows.filter((row) =>
      allowedBlockIds.has(Number(row.block_id))
    )

    if (!missingRows.length && !changedRows.length) {
      designLinkSwitchNotice.value = null
      return
    }

    designLinkSwitchNotice.value = {
      ...notice,
      missingRows,
      changedRows
    }
  }

  return {
    ...baseStore,
    ...selectedItemMixin,
    duplicateDialog,
    duplicateDialogItem,
    openDuplicateDialog,
    closeDuplicateDialog,
    canAddCalculation,
    canChangeCalculation,
    canDeleteCalculation,
    fieldsFiltered,
    createNewCalculation,
    runCalculation,
    resetCalculation,
    validateSwitchDatabase,
    duplicate,
    bulkDuplicate,
    bulkDelete,
    toggleDuplicateDialog,
    duplicateSelectedItems,
    hasMissingDatabaseSwitchIssues,
    setHasMissingDatabaseSwitchIssues,
    designLinkSwitchNotice,
    setDesignLinkSwitchNotice,
    designLinkImportNotice,
    setDesignLinkImportNotice,
    clearDesignLinkImportNoticeForQuantityRow,
    buildingBlockSelectionSwitchNotice,
    setBuildingBlockSelectionSwitchNotice,
    transportConfigSwitchNotice,
    setTransportConfigSwitchNotice,
    pruneDesignLinkSwitchNoticeByBlockIds,
    resetAll
  }
})
